import React, { FC, ReactNode } from 'react';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import {
  Divider,
  Grid,
  Link,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import FlashOnIcon from '@material-ui/icons/FlashOn';

import { Content } from 'api';
import { ListWidget } from 'components';
import { ShareButton } from 'features/content/widgets';
import { getProviderDisplay } from 'features/content/helpers';
import { ContentProvider } from 'providers';
import { generateLink } from 'Links';

import { useClient, useFollowed } from 'hooks';
import { filterShareableContent } from 'features/content/helpers';

interface IProps extends WithStyles<typeof styles> {
  title: string | ReactNode;
  results: Content[];
  link?: string;
}

const styles = (theme: Theme) =>
  createStyles({
    text: {
      paddingLeft: theme.spacing(1),
      paddingRight: 0,
    },
    url: {
      fontSize: theme.typography.pxToRem(12),
    },
    meta: {
      display: 'inline-flex',
      alignItems: 'flex-end',
    },
    icon: {
      marginRight: 2,
    },
    shareButton: {
      paddingRight: 0,
    },
  });

const ContentWidget: FC<IProps> = props => {
  const { classes, results, title, link } = props;
  const { groupRules } = useFollowed();
  const { allow_sharing } = useClient();

  const filteredResults = results.filter(content =>
    filterShareableContent(content, groupRules, allow_sharing),
  );

  function renderContent(content: Content, index: number) {
    if (!content || !content.content_object.url_metadata) {
      return null;
    }

    const { content_id, share_count } = content;
    const { url_metadata } = content.content_object;
    const lastResult = index === filteredResults.length - 1;

    return (
      <ContentProvider key={content_id} value={{ content }}>
        <ListItem disableGutters>
          <ListItemText disableTypography>
            <Link underline="none" component={generateLink(`/content/${content_id}`)}>
              <Typography color="textSecondary" className={classes.url}>
                {getProviderDisplay(content)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                {url_metadata.title}
              </Typography>
            </Link>
            {!!share_count && (
              <Grid container alignItems="center">
                <FlashOnIcon fontSize="inherit" color="disabled" className={classes.icon} />
                <Typography display="inline">{share_count}</Typography>
              </Grid>
            )}
          </ListItemText>
          <ListItemSecondaryAction>
            <ShareButton inverted size="medium" position="end" className={classes.shareButton} />
          </ListItemSecondaryAction>
        </ListItem>
        {!lastResult && <Divider />}
      </ContentProvider>
    );
  }

  return (
    <ListWidget color="primary" title={title} link={link}>
      {filteredResults.map(renderContent)}
    </ListWidget>
  );
};

export default withStyles(styles)(ContentWidget);
